import { useEffect, useMemo, useState, type ReactNode } from "react";
import { FiMail } from "react-icons/fi";
import { useSelector } from "react-redux";
import { Button } from "../components/ui";
import EmailConfigureModal from "../components/Modals/EmailConfigureModal";
import { ownerSelector } from "../store/store";
import { fetchEmailIntegration, type EmailIntegrationStatus } from "../utils";

const outlineBtnClass =
  "h-10 !border-line px-3.5 font-semibold text-dark-blue hover:bg-paper";
const primaryBtnClass = "h-10 px-3.5 font-semibold text-dark-blue";

type ChannelKey = "whatsapp" | "sms" | "email";

type ChannelCard = {
  key: ChannelKey;
  icon: ReactNode;
  title: string;
  provider: string;
  comingSoon?: boolean;
};

const CHANNELS: ChannelCard[] = [
  {
    key: "whatsapp",
    icon: (
      <span
        className="inline-block h-3.5 w-3.5 rounded-full border-[1.5px] border-muted"
        aria-hidden
      />
    ),
    title: "WhatsApp API",
    provider: "Twilio / Meta WhatsApp",
    comingSoon: true,
  },
  {
    key: "sms",
    icon: <span className="text-[13px] font-semibold text-muted">SMS</span>,
    title: "SMS API",
    provider: "Twilio SMS",
    comingSoon: true,
  },
  {
    key: "email",
    icon: <FiMail size={16} className="text-muted" aria-hidden />,
    title: "Email server",
    provider: "SMTP / SendGrid",
  },
];

const cardClass = "flex flex-col rounded-[10px] border border-line bg-white p-[17px]";

const Integrations = () => {
  const { currentUser, currentWorkspace, owner } = useSelector(ownerSelector);
  const [emailConfigureOpen, setEmailConfigureOpen] = useState(false);
  const [emailStatus, setEmailStatus] = useState<EmailIntegrationStatus | null>(null);

  const workspaceId = useMemo(() => {
    if (typeof currentWorkspace !== "string") return currentWorkspace?._id as string | undefined;
    try {
      return JSON.parse(currentWorkspace || "null")?._id as string | undefined;
    } catch {
      return undefined;
    }
  }, [currentWorkspace]);

  const userId = currentUser?.userId as string | undefined;
  const ownerId = owner?._id as string | undefined;
  const defaultTestEmail = String(currentUser?.email || owner?.email || "").trim();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!ownerId && !userId && !workspaceId) return;
      try {
        const status = await fetchEmailIntegration({ ownerId, userId, workspaceId });
        if (!cancelled) setEmailStatus(status);
      } catch {
        if (!cancelled) setEmailStatus(null);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [ownerId, userId, workspaceId]);

  return (
    <section className="noScroll h-full overflow-y-auto overflow-x-hidden bg-paper px-4 py-6 sm:px-9 sm:py-9">
      <div className="mx-auto max-w-[1600px]">
        <header className="mb-[22px]">
          <h1 className="text-[27px] font-semibold tracking-tight text-dark-blue">
            Channel integrations
          </h1>
          <p className="mt-1 text-sm text-muted">
            Configure sending providers. A channel becomes available to campaigns only after InnLead
            verifies a test send.
          </p>
        </header>

        <div className="flex flex-wrap items-center gap-3 rounded-[9px] border border-notice-border bg-notice-bg px-[15px] py-[13px] text-sm">
          <p className="font-semibold text-dark-blue">Verified channels only</p>
          <p className="text-notice-text">
            Campaign actions remain unavailable until credentials are saved and the system
            successfully sends a test through that provider.
          </p>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3.5 md:grid-cols-2 xl:grid-cols-3">
          {CHANNELS.map((channel) => {
            const emailVerified = channel.key === "email" && Boolean(emailStatus?.verified);
            const emailProvider =
              channel.key === "email" && emailStatus?.configured && emailStatus.provider
                ? emailStatus.provider === "sendgrid"
                  ? "SendGrid"
                  : "SMTP"
                : channel.provider;
            const verified = emailVerified;
            return (
              <article key={channel.key} className={cardClass}>
                <div className="flex h-4 items-center text-muted">{channel.icon}</div>
                <h2 className="mt-[18px] text-[15px] font-semibold tracking-tight text-dark-blue">
                  {channel.title}
                </h2>
                <p className="mt-1 text-[11px] text-muted">{emailProvider}</p>
                <p
                  className={`mt-1 text-[10px] font-semibold ${
                    channel.comingSoon
                      ? "text-muted"
                      : verified
                        ? "text-success"
                        : "text-warning"
                  }`}
                >
                  ●{" "}
                  {channel.comingSoon
                    ? "Coming soon"
                    : verified
                      ? "Verified · test email delivered"
                      : "Needs verification · send a test"}
                </p>
                <div className="mt-auto pt-4">
                  <Button
                    variant={channel.comingSoon || verified ? "outline" : "primary"}
                    size="sm"
                    isDisabled={channel.comingSoon}
                    className={channel.comingSoon || verified ? outlineBtnClass : primaryBtnClass}
                    onClick={() => {
                      if (channel.key === "email") setEmailConfigureOpen(true);
                    }}
                  >
                    {channel.comingSoon ? "Coming soon" : "Configure"}
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <EmailConfigureModal
        isOpen={emailConfigureOpen}
        onClose={() => setEmailConfigureOpen(false)}
        defaultTestEmail={defaultTestEmail}
        existing={emailStatus}
        ownerId={ownerId}
        userId={userId}
        workspaceId={workspaceId}
        onUpdated={setEmailStatus}
      />
    </section>
  );
};

export default Integrations;
